import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useCreateGuestPassShareLink } from "@/hooks/guestPasses";
import { GuestPassesQueryKey } from "@/hooks/useHomeData";

import FormField from "../FormField";
import FormFields from "../FormFields";
import Header from "../Header";
import Icon from "../Icon";
import { Textarea } from "../Input";
import Label from "../Label";
import Loader from "../Loader";
import { useNotification } from "../Notification";
import { getDaysTillExpiry, getErrorMessage, GuestPass } from ".";
import GuestPassRestrictionsAndFAQs from "./GuestPassRestictionsAndFAQs";
import { btnClasses, btnIconClasses, labelClasses } from "./Styles";

type Props = {
  pass: GuestPass;
};

export default function SharePassByCode({ pass }: Props) {
  const notify = useNotification();
  const [shareMsg, setShareMsg] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const getGuestPassShareLink = useCreateGuestPassShareLink();

  const expiresDisplay = useMemo(() => {
    return getDaysTillExpiry(pass.expiresDate) ?? "";
  }, [pass.expiresDate]);

  if (getGuestPassShareLink.isError) {
    return null;
  }

  const createLink = () => {
    getGuestPassShareLink.mutate(pass.promoCodeId, {
      onSuccess: ({ shareCopyableText }) => {
        setShareMsg(shareCopyableText);
        void queryClient.invalidateQueries({ queryKey: [GuestPassesQueryKey] });
      },
      onError: (error) => {
        notify(getErrorMessage(error.message), "error");
      },
    });
  };

  const copyLink = async () => {
    if (shareMsg !== null) {
      await navigator.clipboard.writeText(shareMsg);
      notify("Link copied to clipboard", "success");
    }
  };

  const showGenerateLinkStep = shareMsg === null;

  return (
    <>
      <Header className="mb-4 text-[22px] text-clear-sky">
        Share Your Guest Pass
      </Header>
      <div className="mb-6 flex items-center justify-end">{expiresDisplay}</div>
      {showGenerateLinkStep && (
        <>
          {getGuestPassShareLink.isPending && <Loader />}
          {!getGuestPassShareLink.isPending && (
            <button className={btnClasses} onClick={createLink}>
              <Icon name="cycle" className={btnIconClasses} />
              Generate Link
            </button>
          )}
          <GuestPassRestrictionsAndFAQs pass={pass} />
        </>
      )}
      {!showGenerateLinkStep && (
        <>
          <FormFields className="mb-4">
            <FormField>
              <Label className={labelClasses} htmlFor="link">
                One time Link
              </Label>
              <Textarea
                id="link"
                className="rounded border-green-tea bg-child-of-light pt-8 text-sm leading-loose disabled:text-almost-black"
                rows={4}
                disabled
                value={shareMsg}
              />
            </FormField>
          </FormFields>
          <div className="mb-6">
            <button onClick={copyLink} className={btnClasses}>
              <Icon name="copy" className={btnIconClasses} />
              Copy Link
            </button>
          </div>
          <GuestPassRestrictionsAndFAQs pass={pass} />
        </>
      )}
    </>
  );
}
